import type { TranslateFunction } from "~/kernel/internationalization";
import type { Service } from "../models/Service";
import formatDuration from "~/kernel/dateTime/formatDuration";
import formatTime from "./formatTime";
import { Money } from "~/kernel/money";
import { getStepPrice } from "~/booking/helpers/getStepPrice";
import sortBy from "~/kernel/arrays/sortBy";

export function explainPricing(
  service: Service,
  { t }: { t: TranslateFunction },
) {
  if (service.pricings.length === 0) {
    return [
      t("explainPricing.unknownPrice"),
      t("explainPricing.estimatedDuration", {
        duration: formatTime(service),
      }),
    ].join(" ");
  }
  return [
    ...sortBy(service.pricings, (p) => p.minTime).map((pricing, i) => {
      const isOnly = service.pricings.length === 1;
      const isLast = i === service.pricings.length - 1;
      const isFirst = i === 0;

      if (isOnly) {
        switch (pricing.type) {
          case "fixed":
            return t("explainPricing.singlePrice", {
              interpolation: { escapeValue: false },
              price: Money.fromFloat(pricing.amount).format({
                includeDecimals: false,
              }),
            });
          case "durationBased":
            if (pricing.step === 1) {
              return t("explainPricing.singlePrice", {
                interpolation: { escapeValue: false },
                price:
                  Money.fromFloat(pricing.amount).format({
                    includeDecimals: false,
                  }) + " / h",
              });
            }

            return t("explainPricing.singleStepPrice", {
              interpolation: { escapeValue: false },
              stepDuration: formatDuration(pricing.step),
              price: Money.fromFloat(getStepPrice(pricing)).format({
                includeDecimals: false,
              }),
            });
        }
      }

      if (isFirst) {
        switch (pricing.type) {
          case "fixed":
            return t("explainPricing.basePrice", {
              price: Money.fromFloat(pricing.amount).format({
                includeDecimals: false,
              }),
            });
          case "durationBased":
            if (pricing.step === 1) {
              return t("explainPricing.singlePrice", {
                interpolation: { escapeValue: false },
                price:
                  Money.fromFloat(pricing.amount).format({
                    includeDecimals: false,
                  }) + " / h",
              });
            }

            return t("explainPricing.baseStepPrice", {
              stepDuration: formatDuration(pricing.step),
              price: Money.fromFloat(getStepPrice(pricing)).format({
                includeDecimals: false,
              }),
            });
        }
      }

      if (isLast) {
        switch (pricing.type) {
          case "durationBased": {
            if (pricing.step === 1) {
              return t("explainPricing.lastPrice", {
                interpolation: { escapeValue: false },
                durationThreshold: formatDuration(pricing.minTime),
                price:
                  Money.fromFloat(pricing.amount).format({
                    includeDecimals: false,
                  }) + " / h",
              });
            }

            return t("explainPricing.lastStepPrice", {
              interpolation: { escapeValue: false },
              durationThreshold: formatDuration(pricing.minTime),
              stepDuration: formatDuration(pricing.step),
              price: Money.fromFloat(getStepPrice(pricing)).format({
                includeDecimals: false,
              }),
            });
          }
          case "fixed": {
            return t("explainPricing.lastPrice", {
              interpolation: { escapeValue: false },
              durationThreshold: formatDuration(pricing.minTime),
              price: Money.fromFloat(pricing.amount).format({
                includeDecimals: false,
              }),
            });
          }
        }
      }

      switch (pricing.type) {
        case "durationBased": {
          if (pricing.step === 1) {
            const nextPricing = service.pricings[i + 1];
            return t("explainPricing.middlePrice", {
              interpolation: { escapeValue: false },
              timeRange: formatTime({
                minTime: pricing.minTime,
                maxTime: nextPricing.minTime,
              }),
              price:
                Money.fromFloat(pricing.amount).format({
                  includeDecimals: false,
                }) + " / h",
            });
          }

          const nextPricing = service.pricings[i + 1];
          return t("explainPricing.middleStepPrice", {
            interpolation: { escapeValue: false },
            timeRange: formatTime({
              minTime: pricing.minTime,
              maxTime: nextPricing.minTime,
            }),
            stepDuration: formatDuration(pricing.step),
            price: Money.fromFloat(getStepPrice(pricing)).format({
              includeDecimals: false,
            }),
          });
        }
        default:
        case "fixed": {
          const nextPricing = service.pricings[i + 1];
          return t("explainPricing.middlePrice", {
            interpolation: { escapeValue: false },
            timeRange: formatTime({
              minTime: pricing.minTime,
              maxTime: nextPricing.minTime,
            }),
            price: Money.fromFloat(pricing.amount).format({
              includeDecimals: false,
            }),
          });
        }
      }
    }),
    t("explainPricing.estimatedDuration", {
      duration: formatTime(service),
      interpolation: { escapeValue: false },
    }),
  ].join(" ");
}
